import { observable, action } from 'mobx';
import axios from 'axios/index';
import { Common as util } from '../utils/common';
import gql from '../utils/gql';

export class AdminLog {

  @observable isLoading = false;
  @observable stats = null;
  @observable topPages = [];
  @observable trend = [];

  // 1) 페이지뷰 기록 — fire-and-forget
  @action logPageView = (data) => {
    const property = ['path', 'mainMenu', 'subMenu', 'pageLabel', 'referrer', 'query',
                      'type', 'eventName', 'extra', 'durationMs', 'userAgent', 'sessionId', 'shopNo'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'mutation',
      operation: 'logAdminAccess',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams).catch(() => {
      // 의도적 무시: 로그 실패가 UX에 영향 주면 안 됨
    });
  };

  // 1-b) 액션 이벤트 기록 — keepalive fetch (네비게이션/리로드 중에도 유실 방지)
  //   reviewList 등에서 trackAction 직후 window.location 리로드(doSearch)가 일어나면
  //   일반 XHR(axios)은 in-flight 상태로 abort 되어 서버에 도달하지 못함.
  //   keepalive:true 는 페이지 언로드 후에도 요청 전송을 보장 (sendBeacon 과 달리 헤더 설정 가능 → JWT 인증 유지).
  @action logActionBeacon = (data) => {
    try {
      const property = ['path', 'mainMenu', 'subMenu', 'pageLabel', 'referrer', 'query',
                        'type', 'eventName', 'extra', 'durationMs', 'userAgent', 'sessionId', 'shopNo'];
      const variables = util.getDataStByKey(data, property);
      const params = {
        type: 'mutation',
        operation: 'logAdminAccess',
        variables: variables,
        getData: 'data'
      };
      const gpqParams = gql(params);
      // 앱 axios 와 동일한 baseURL/인증/타겟서버 헤더를 그대로 사용
      const base = (axios.defaults && axios.defaults.baseURL) || '';
      const common = (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) || {};
      const headers = { 'Content-Type': 'application/json' };
      ['Authorization', 'rfAuthorization', 'X-Target-Server'].forEach(k => { if (common[k]) headers[k] = common[k]; });
      return fetch(base + '/', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(gpqParams),
        keepalive: true
      }).catch(() => {});
    } catch (e) {
      // 의도적 무시: 로그 실패가 UX에 영향 주면 안 됨
    }
  };

  // 2) KPI (총 PV, UV 사용자, UV 스토어, 평균 체류)
  @action getAdminAccessStats = (data, cb) => {
    const property = ['store_id', 'startDate', 'endDate', 'includeSuper'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'query',
      operation: 'getAdminAccessStats',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams)
      .then(function (res) {
        if (res.status === 200) {
          const d = res.data.data[params.operation];
          if (typeof cb === 'function') cb(d);
          else return d;
        }
      })
      .catch(function (error) {
        console.log(error);
        if (typeof cb === 'function') cb(null);
        return error;
      });
  };

  // 3) 페이지별 TOP
  @action getAdminAccessTopPages = (data, cb) => {
    const property = ['store_id', 'startDate', 'endDate', 'limit', 'includeSuper', 'sortBy'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'query',
      operation: 'getAdminAccessTopPages',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams)
      .then(function (res) {
        if (res.status === 200) {
          const d = res.data.data[params.operation];
          if (typeof cb === 'function') cb(d);
          else return d;
        }
      })
      .catch(function (error) {
        console.log(error);
        if (typeof cb === 'function') cb(null);
        return error;
      });
  };

  // 4) reviewList 기능 사용 분석 (이벤트별 사용량 + 채택률)
  @action getReviewListFeatureUsage = (data, cb) => {
    const property = ['store_id', 'startDate', 'endDate', 'includeSuper'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'query',
      operation: 'getReviewListFeatureUsage',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams)
      .then(function (res) {
        if (res.status === 200) {
          const d = res.data.data[params.operation];
          if (typeof cb === 'function') cb(d);
          else return d;
        }
      })
      .catch(function (error) {
        console.log(error);
        if (typeof cb === 'function') cb(null);
        return error;
      });
  };

  // 5) 데이터 리셋 (슈퍼어드민 전용)
  @action resetAdminAccessLogs = (data, cb) => {
    const property = ['store_id', 'startDate', 'endDate'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'mutation',
      operation: 'resetAdminAccessLogs',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams)
      .then(function (res) {
        if (res.status === 200) {
          const d = res.data.data[params.operation];
          if (typeof cb === 'function') cb(d);
          else return d;
        }
      })
      .catch(function (error) {
        console.log(error);
        if (typeof cb === 'function') cb(null);
        return error;
      });
  };

  // 4) 시간대별 트렌드 (일/시 단위)
  @action getAdminAccessTrend = (data, cb) => {
    const property = ['store_id', 'startDate', 'endDate', 'granularity', 'includeSuper'];
    const variables = util.getDataStByKey(data, property);
    const params = {
      type: 'query',
      operation: 'getAdminAccessTrend',
      variables: variables,
      getData: 'data'
    };
    const gpqParams = gql(params);
    return axios.post('/', gpqParams)
      .then(function (res) {
        if (res.status === 200) {
          const d = res.data.data[params.operation];
          if (typeof cb === 'function') cb(d);
          else return d;
        }
      })
      .catch(function (error) {
        console.log(error);
        if (typeof cb === 'function') cb(null);
        return error;
      });
  };
}
